import React from 'react'
import { Button, Table, Th, Td, Tr, Badge } from '@/shared/ui'
import { listAffiliatePayouts, finalizePayout, cancelPayout } from '../services/affiliatePayoutService'
import GeneratePayoutModal from './GeneratePayoutModal'

function cents(v?: number | null) {
  return `R$ ${(Number(v || 0) / 100).toFixed(2)}`
}
function iso(v?: string | null) {
  return v ? new Date(v).toLocaleString() : '-'
}

type Props = {
  affiliateUserId: string
  refreshKey?: number
}

export default function AffiliatePayoutsPanel({ affiliateUserId, refreshKey }: Props) {
  const [items, setItems] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [openGen, setOpenGen] = React.useState(false)

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setItems(await listAffiliatePayouts(affiliateUserId))
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar payouts')
    } finally {
      setLoading(false)
    }
  }, [affiliateUserId])

  React.useEffect(() => {
    load()
  }, [load, refreshKey])

  async function onConfirm(ids: string[], from?: string | null, to?: string | null) {
    try {
      const { supabase } = await import('@/lib/supabase')
      const { data: session } = await supabase.auth.getSession()
      const token = session?.session?.access_token
      const base: any = (import.meta as any).env?.VITE_SUPABASE_URL || ''
      const url = base.replace(/\/$/, '') + '/functions/v1/admin-affiliate-create-payout'
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          affiliate_user_id: affiliateUserId,
          commission_ids: ids,
          from: from || null,
          to: to || null,
        }),
      })
      if (!res.ok) throw new Error(await res.text())
      await load()
    } catch (e: any) {
      setError(e?.message || 'Falha ao gerar payout')
    }
  }

  async function onFinalize(id: string) {
    try {
      await finalizePayout(id)
      await load()
    } catch (e: any) {
      setError(e?.message || 'Falha ao marcar como pago')
    }
  }

  async function onCancel(id: string) {
    try {
      await cancelPayout(id)
      await load()
    } catch (e: any) {
      setError(e?.message || 'Falha ao cancelar payout')
    }
  }

  return (
    <div className="mt-8">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-base font-medium">Payouts</div>
        <Button onClick={() => setOpenGen(true)} disabled={loading}>
          Gerar payout (earneds)
        </Button>
      </div>
      <GeneratePayoutModal
        affiliateUserId={affiliateUserId}
        open={openGen}
        onClose={() => setOpenGen(false)}
        onConfirm={onConfirm}
      />
      {error && (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {error}
        </div>
      )}
      <Table>
        <thead>
          <Tr>
            <Th>Data</Th>
            <Th>Status</Th>
            <Th>Valor</Th>
            <Th>Taxa</Th>
            <Th>Agendado</Th>
            <Th>Pago em</Th>
            <Th className="w-48 text-right">Acoes</Th>
          </Tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <Tr key={p.id}>
              <Td>{iso(p.created_at)}</Td>
              <Td>
                <Badge variant="soft" className="capitalize">
                  {p.status}
                </Badge>
              </Td>
              <Td>{cents(p.amount_cents)}</Td>
              <Td>{cents(p.fee_cents)}</Td>
              <Td>{iso(p.scheduled_for)}</Td>
              <Td>{iso(p.paid_at)}</Td>
              <Td className="space-x-2 text-right">
                <Button variant="outline" disabled={p.status === 'paid'} onClick={() => onFinalize(p.id)}>
                  Marcar Pago
                </Button>
                <Button variant="outline" disabled={p.status === 'paid'} onClick={() => onCancel(p.id)}>
                  Cancelar
                </Button>
              </Td>
            </Tr>
          ))}
          {items.length === 0 && (
            <Tr>
              <Td colSpan={7} className="py-6 text-center text-sm text-foreground/70">
                Nenhum payout.
              </Td>
            </Tr>
          )}
        </tbody>
      </Table>
    </div>
  )
}

